import { useAppStore } from '@/lib/store';
import type { WornClothingType } from '@/types/measurements';
import { StepProgress } from './ui/StepProgress';

const STEPS = ['Clothing', 'Height', 'Photo', 'Results'];

interface ClothingOption {
  type: WornClothingType;
  label: string;
  description: string;
  accuracy: string;
}

const CLOTHING_OPTIONS: ClothingOption[] = [
  {
    type: 'tight_fitness',
    label: 'Tight / Fitness',
    description: 'Gym wear, leggings, compression',
    accuracy: 'Best accuracy',
  },
  {
    type: 'underwear',
    label: 'Underwear',
    description: 'Minimal clothing',
    accuracy: 'Best accuracy',
  },
  {
    type: 'tshirt_jeans',
    label: 'T-shirt & Jeans',
    description: 'Casual everyday wear',
    accuracy: 'Good accuracy',
  },
  {
    type: 'dress_shirt',
    label: 'Dress Shirt',
    description: 'Office / smart casual',
    accuracy: 'Good accuracy',
  },
  {
    type: 'light_traditional',
    label: 'Light Traditional',
    description: 'Kaftan, dashiki, light agbada',
    accuracy: 'Fair accuracy',
  },
  {
    type: 'heavy_traditional',
    label: 'Heavy Traditional',
    description: 'Full agbada, heavy aso-oke',
    accuracy: 'Lower accuracy',
  },
];

const ACCURACY_COLORS: Record<string, string> = {
  'Best accuracy': 'text-green-600',
  'Good accuracy': 'text-brand-600',
  'Fair accuracy': 'text-yellow-600',
  'Lower accuracy': 'text-red-500',
};

export function ClothingSelector() {
  const { wornClothing, setWornClothing, setStep } = useAppStore();

  function handleSelect(type: WornClothingType) {
    setWornClothing(type);
  }

  function handleNext() {
    if (wornClothing) {
      setStep('reference');
    }
  }

  return (
    <div className="screen">
      <StepProgress steps={STEPS} currentIndex={0} />

      <div className="mt-6 flex-1">
        <h2 className="text-2xl font-bold text-gray-900">
          What are you wearing?
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          We compensate for clothing thickness to improve accuracy.
        </p>

        <div className="mt-6 space-y-3">
          {CLOTHING_OPTIONS.map((option) => (
            <button
              key={option.type}
              onClick={() => handleSelect(option.type)}
              className={`w-full rounded-xl border-2 p-4 text-left transition-all active:scale-[0.98] ${
                wornClothing === option.type
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{option.label}</p>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
                <span
                  className={`text-xs font-medium ${ACCURACY_COLORS[option.accuracy] ?? 'text-gray-500'}`}
                >
                  {option.accuracy}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <button
          onClick={handleNext}
          disabled={!wornClothing}
          className="btn-primary"
        >
          Continue
        </button>
        <button onClick={() => setStep('welcome')} className="btn-secondary">
          Back
        </button>
      </div>
    </div>
  );
}
